import { CampaignData } from '@/models/Campaign';
import { City, District } from '@/models/City';
import { User } from '@/models/User';
import {
  getLocalUserForApp,
  getTokenForApp,
  removeTokenForApp,
  setLocalUserForApp,
} from './CookieUtils';
import { decryptAES, encryptAES } from './crypto/AES';
import {
  readMigratedItem,
  removeMigratedItem,
  writeMigratedItem,
} from './crypto/storageMigration';
import { appRPC, checkRPC } from './utils';
import { WindowDispatchEvent } from './WindowDispatchEvent';
import { safeLocalStorage } from './safeStorage';
import { STORAGE_KEY } from './storageKeys';

export { STORAGE_KEY };

let intentionalNativeTokenClear = false;

export function markIntentionalNativeTokenClear() {
  intentionalNativeTokenClear = true;
}

export const notifyUserData = (user?: User, token?: string | undefined) => {
  try {
    if (!checkRPC('bridge')) return;

    let tokenToSend = token;
    if (!tokenToSend) {
      tokenToSend = getTokenForApp();
    }

    const payload = {
      user: user
        ? {
            id: user.id,
            title: user.title,
            email: user.email,
            msisdn: user.msisdn,
            birthdate: user.birthdate,
            isAnonymous: user.isAnonymous,
            badgeCount: user.badgeCount,
          }
        : null,
      token: tokenToSend || null,
    };

    appRPC('bridge', 'updateUserData', payload);
  } catch (err) {}
};

const notifyNotificationBadge = (badgeCount: number, setFrom?: string) => {
  try {
    if (!checkRPC('bridge')) return;
    appRPC('bridge', 'updateNotificationBadge', {
      badgeCount: Number(badgeCount || 0),
      setFrom: setFrom || 'local',
    });
  } catch (err) {}
};

export const getIsAnonymous = (): boolean => {
  const anonymousValue = readMigratedItem(safeLocalStorage, STORAGE_KEY.IS_ANONYMOUS);
  return anonymousValue ? decryptAES(anonymousValue) !== 'false' : true;
};

export const setIsAnonymous = (isAnonymous: boolean) => {
  writeMigratedItem(safeLocalStorage, STORAGE_KEY.IS_ANONYMOUS, encryptAES(isAnonymous.toString()));
  window.dispatchEvent(new Event(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE));
  const u = getLocalUserForApp();
  if (u) {
    const merged = { ...u, isAnonymous: isAnonymous ? 1 : 0 };
    notifyUserData(merged);
    notifyNotificationBadge(isAnonymous ? 0 : Number(u.badgeCount || 0), 'setIsAnonymous');
  } else {
    notifyUserData(undefined);
    notifyNotificationBadge(0, 'setIsAnonymous');
  }
};

export const getLastSelectedCity = ():
  | {
      city: City | undefined;
      district: District | undefined;
    }
  | undefined => {
  try {
    const cityValue = readMigratedItem(safeLocalStorage, STORAGE_KEY.LAST_SELECTED_CITY);
    if (!cityValue) return undefined;
    const decrypted = decryptAES(cityValue);
    if (!decrypted) return undefined;
    return JSON.parse(decrypted);
  } catch {
    return undefined;
  }
};

export const setLastSelectedCity = (city: City | undefined, district: District) => {
  const lastSelectedCity = getLastSelectedCity();

  writeMigratedItem(
    safeLocalStorage,
    STORAGE_KEY.LAST_SELECTED_CITY,
    encryptAES(
      JSON.stringify({
        city,
        district,
      }),
    ),
  );

  if (lastSelectedCity?.district?.id !== district.id) {
    window.dispatchEvent(new Event(WindowDispatchEvent.UPDATE_SELECTED_CITY));
  }
};

export const getLocalUser = (): User | undefined => {
  try {
    const userValue = readMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_USER);
    if (!userValue) return undefined;
    const decrypted = decryptAES(userValue);
    if (!decrypted) return undefined;
    return JSON.parse(decrypted);
  } catch {
    return undefined;
  }
};

export const setLocalUser = (user: User | undefined) => {
  if (!user) {
    removeMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_USER);
    safeLocalStorage.removeItem(STORAGE_KEY.LOCAL_USER_NON_ENCRYPTED);
    notifyUserData(undefined);
    notifyNotificationBadge(0, 'setLocalUser-clear');
    window.dispatchEvent(new Event(WindowDispatchEvent.UPDATE_USER));
    return;
  }

  writeMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_USER, encryptAES(JSON.stringify(user)));
  safeLocalStorage.setItem(
    STORAGE_KEY.LOCAL_USER_NON_ENCRYPTED,
    JSON.stringify({
      email: user.email,
      title: user.title,
      mobilePhone: `90${user.msisdn.slice(-10)}`,
      isAnonymous: user.isAnonymous === 1,
    }),
  );
  setLocalUserForApp(user);

  notifyUserData(user);
  notifyNotificationBadge(Number(user.badgeCount || 0), 'setLocalUser');
  window.dispatchEvent(new Event(WindowDispatchEvent.UPDATE_USER));
};

export const getLocalToken = (): string | undefined => {
  const localTokenValue = readMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_TOKEN);
  return localTokenValue ? decryptAES(localTokenValue) : undefined;
};

export const setLocalToken = (
  token: string | undefined,
  options?: { skipNativeNotify?: boolean },
) => {
  if (!token) {
    removeMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_TOKEN);
    removeTokenForApp();
    const shouldNotifyNative =
      !options?.skipNativeNotify && (!checkRPC('bridge') || intentionalNativeTokenClear);
    if (shouldNotifyNative) {
      notifyUserData(getLocalUser(), undefined);
    }
    intentionalNativeTokenClear = false;
    return;
  }
  writeMigratedItem(safeLocalStorage, STORAGE_KEY.LOCAL_TOKEN, encryptAES(token));
  notifyUserData(getLocalUser(), token);
};

export const getCampaignName = (): string | undefined => {
  return undefined;
  // try {
  //   const localCampaign = encryptMD5(STORAGE_KEY.CAMPAIGN_NAME);
  //   const campaignData = JSON.parse(localStorage.getItem(localCampaign) || '{}') as CampaignData;
  //   if (new Date() > new Date(campaignData.validUntil)) {
  //     localStorage.removeItem(localCampaign);
  //     return undefined;
  //   }
  //   return campaignData.title;
  // } catch (error) {
  //   return undefined;
  // }
};

export const setCampaignName = (campaignData: CampaignData) => {
  writeMigratedItem(
    safeLocalStorage,
    STORAGE_KEY.CAMPAIGN_NAME,
    JSON.stringify({
      title: campaignData.title,
      validUntil: campaignData.validUntil,
    }),
  );
};

export function isAppClient(): boolean {
  if (typeof document === 'undefined') return false;
  const ds = document.documentElement.dataset.platform || '';
  const ls = safeLocalStorage.getItem('platform') || '';
  const ck = document.cookie || '';
  return ds.includes('app') || ls.includes('app') || ck.includes('platform=app');
}
